"use client";

import { ArrowLeft, Menu, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/GlobalSearch";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { UserNav } from "@/components/UserNav";
import { useSidebarContext } from "@/components/ui/sidebar";
import { useIssues } from "@/lib/hooks/useIssues";
import { CreateIssue } from "@/components/issues/CreateIssue";
import { NotificationButton } from "@/components/notifications/NotificationDropdown";
import { Separator } from "@/components/ui/separator";
import Link from 'next/link';

interface HeaderProps {
  showBackButton?: boolean;
  showLogo?: boolean;
  showGlobalSearch?: boolean;
  showHamburger?: boolean;
  onBackClick?: () => void;
  actions?: React.ReactNode;
}

export function Header({ 
  showBackButton, 
  showLogo,
  showGlobalSearch,
  showHamburger,
  onBackClick,
  actions 
}: HeaderProps) {
  const { setCollapsed, collapsed } = useSidebarContext();
  const { issues } = useIssues();
  const activeIssues = issues.filter(issue => issue.status !== 'Done' && issue.status !== 'Archived');

  return (
    <div className="sticky top-0 z-50">
      <header className="relative">
        {/* Gradient line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
        
        {/* Main container with backdrop and border */}
        <div className="relative border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center gap-4 px-4 lg:px-8">
            {/* Left Section */}
            <div className="flex items-center gap-4">
              {showHamburger && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCollapsed(!collapsed)}
                  className="h-9 w-9 rounded-full transition-all hover:bg-muted"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              )}

              {showBackButton && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onBackClick}
                  className="h-9 w-9 rounded-full transition-all hover:bg-muted"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              )}
              
              {showLogo && (
                <div className="hidden md:block pr-4 border-r border-border/40">
                  <Logo className="h-8" />
                </div>
              )}
            </div>
            
            {/* Center Section - Search */}
            {showGlobalSearch && (
              <div className="flex-1 max-w-2xl mx-auto">
                <div className="relative group">
                  <GlobalSearch />
                  <div className="absolute inset-0 -m-1 group-focus-within:ring-2 ring-primary/20 rounded-lg transition-all" />
                  <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </div>
              </div>
            )}
            
            {/* Right Section */}
            <div className="flex items-center gap-2 ml-auto">
              {/* Tools Section */}
              <div className="flex items-center gap-1.5">
                <CreateIssue>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-9 w-9 rounded-full transition-all hover:bg-muted"
                  >
                    <AlertCircle className="h-5 w-5 text-muted-foreground transition-colors" />
                    {activeIssues.length > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 text-[10px] font-medium rounded-full bg-red-500 text-white flex items-center justify-center animate-pulse">
                        {activeIssues.length}
                      </span>
                    )}
                  </Button>
                </CreateIssue>
                
                <NotificationButton 
                  className="h-9 w-9 rounded-full transition-all hover:bg-muted" 
                  iconClassName="h-5 w-5 text-muted-foreground" 
                />
                
                <ThemeToggle 
                  className="h-9 w-9 rounded-full transition-all hover:bg-muted" 
                  iconClassName="h-5 w-5 text-muted-foreground" 
                />
              </div>

              <Separator orientation="vertical" className="h-6 hidden md:block" />
              
              {/* User Section */}
              <div className="flex items-center gap-3">
                <div className="hidden md:block text-right">
                  <div className="text-sm font-medium">Workspace</div>
                  <div className="text-xs text-muted-foreground">TerraSync Pro</div>
                </div>
                <UserNav 
                  className="h-9 w-9 rounded-full ring-offset-background transition-all hover:bg-muted" 
                  iconClassName="h-5 w-5" 
                />
              </div>
            </div>

            {actions && (
              <>
                <Separator orientation="vertical" className="h-6 hidden md:block" />
                <div className="ml-2">{actions}</div>
              </>
            )}
          </div>
        </div>
      </header>
    </div>
  );
} 